
    import Vue from "vue";
    import {Component, Prop, Watch} from "vue-property-decorator";
    import { VueInject } from "@/VueIntegration/VueInjectable";
    import Modal from "../UI/Modal.vue"
    import { TutorialAttemptApi } from "@/Api/TutorialAttemptApi";
    import {Slideshow} from "@/slideshow";
    import VueButton from "../UI/VueButton.vue";
    import ResultModal from "./ResultModal.vue";
    import {render} from "@/Core/CustomisationRenderer";
    import {shuffleArray} from "@/Utility";
    import {announce} from "@/UI/Announce";
    import Tick from "../Icons/Tick.vue";
    import Cross from "../Icons/Cross.vue";
    import {
        IAskMultipleChoiceQuestion,
        ICompletedMultipleChoiceQuestion,
        IMultipleChoiceOption
    } from "@/Core/Interactions";
    import {IAppearance} from "@/Core/TutorialVariables";
    import {TutorialState} from "@/Core/TutorialState";


    @Component({
        components: {
            Cross,
            Tick,
            Modal,
            VueButton,
            ResultModal
        }
    })
    export default class MultipleChoice extends Vue {
        //Props
        @Prop({ type: String, required: true }) readonly id: string;
        @Prop({ type: String, required: false }) readonly code: string;
        @Prop({}) media: string;
        @Prop({ type: String, default: "random"}) order: string;
        @Prop({type: Boolean, required: true}) feedback: boolean;

        //Services
        @VueInject("Slideshow") readonly slideshow: Slideshow;
        @VueInject("TutorialAttemptApi") readonly api: TutorialAttemptApi;
        @VueInject("TutorialState") state: TutorialState;

        //State
        question: IAskMultipleChoiceQuestion|null = null;
        options: {itemText: string, code: string}[] = [];
        selectedCodes: string|string[] = [];
        completedQuestion: ICompletedMultipleChoiceQuestion|null = null;
        showResultModal = false;
        showAccessibilityModal = false;
        appearance: IAppearance;
        isSubmittingAnswers = false;
        errorMessage: string|null = null;
        lockId: number|null = null;

        // computed

        get inputType() {
            return this.question!.maxAnswers > 1 ? 'checkbox' : 'radio';
        }

        get isAnswered() {
            return this.completedQuestion != null;
        }

        get questionCode() {
            return this.code || this.id;
        }

        get selectedCodesArray(): string[] {
            if (typeof this.selectedCodes === "string")
                return [this.selectedCodes];
            else
                return this.selectedCodes;
        }

        get isValidSelection(): boolean {
            let selectedCodes = this.selectedCodesArray;
            return selectedCodes.length >= this.question!.minAnswers && selectedCodes.length <= this.question!.maxAnswers;
        }

        get hasMedia(): boolean {
            return this.media.trim().length > 0;
        }

        get renderedMedia() {
            return render(this.media, this.state);
        }

        created() {
            this.appearance = this.slideshow.appearance;
        }

        // methods
        mounted() {
            this.enter();
        }

        @Watch('id')
        watchId() {
            this.enter();
        }

        enter() {
            this.question = null;
            this.options = [];
            this.selectedCodes = [];
            this.showResultModal = false;
            this.isSubmittingAnswers = false;
            this.errorMessage = null;
            this.lockId = null;

            this.question = <IAskMultipleChoiceQuestion> this.api.getQuestion(this.questionCode);
            for (let code in this.question.items) {
                let item = this.question.items[code];
                this.options.push({
                    itemText: item.itemText,
                    code: item.code
                });
            }

            if (this.order === 'a-z') {
                this.options.sort((a, b) => a.itemText.localeCompare(b.itemText));
            }
            else if (this.order === 'z-a') {
                this.options.sort((a, b) => -a.itemText.localeCompare(b.itemText));
            }
            else {
                shuffleArray(this.options);
            }

            this.completedQuestion = <ICompletedMultipleChoiceQuestion> this.api.getCompletedQuestion(this.questionCode);
            if (this.completedQuestion) {
                this.selectedCodes = this.completedQuestion.givenAnswers.map(a => a.questionAnswerCode);
                announce("Slide featuring a multiple choice question that has been completed.");
            }
            else {
                this.lockId = this.slideshow.lockNext();
                announce("Slide featuring a multiple choice question to answer. Please go to the main region of the page.");
            }

            window.scrollTo(0, 0);
            this.slideshow.slideEntered();
        }

        @Watch('selectedCodes')
        onSelect(newCodes: string|string[], oldCodes: string|string[]) {
            if (typeof newCodes === 'string')
                return;

            if (newCodes.length > this.question!.maxAnswers) {
                this.selectedCodes = oldCodes;
            }
        }

        isCodeSelected(code: string) {
            if (typeof (this.selectedCodes) === 'string') {
                return this.selectedCodes === code;
            }
            else {
                return this.selectedCodes.indexOf(code) > -1;
            }
        }

        isCodeCorrect(code: string): boolean|null {
            if (this.completedQuestion == null)
                return null;

            return this.completedQuestion.correctAnswers.find(a => a.code === code) != null;
        }
        
        //Methods
        getInputId(option: IMultipleChoiceOption) {
            return this.question!.questionCode + '-' + option.code;
        }

        getInputName(option: IMultipleChoiceOption) {
            if (this.inputType === 'radio')
                return 'question-' + this.questionCode;

            return 'question-' + this.questionCode + '-' + option.code;
        }

        submitAnswers() {
            if (this.isSubmittingAnswers || this.completedQuestion)
                return;

            let toSubmit = this.selectedCodesArray;

            if (toSubmit.length > this.question!.maxAnswers) {
                this.errorMessage = "Too many answers selected.";
                return;
            }

            if (toSubmit.length < this.question!.minAnswers) {
                this.errorMessage = "Too few answers selected.";
                return;
            }

            this.isSubmittingAnswers = true;
            this.api.submitMultipleChoice(this.questionCode, toSubmit)
                .then(result => {
                    this.isSubmittingAnswers = false;
                    this.completedQuestion = result;
                    if (this.lockId != null)
                        this.slideshow.unlockNext(this.lockId);

                    if (this.feedback) {
                        this.showResultModal = true;
                    }
                    else {
                        this.slideshow.nextSlide();
                    }
                })
                .catch(e => {
                    console.error(e);
                    this.errorMessage = "Sorry, there's been an error processing your request. " +
                        "Please try refreshing this page and attempting to answer the question again. " +
                        "If the error persists, please contact " + this.state.evaluate('customer.support_email');
                });
        }

        isScrollToBlocked = false;

        blockScrollTo() {
            this.isScrollToBlocked = true;
        }

        scrollTo(event: FocusEvent) {
            if (this.isScrollToBlocked)
                return;

            let target = <HTMLElement> event.target;
            target.scrollIntoView({block: "center"});
        }

        finished() {
            this.slideshow.nextSlide();
        }
    }
